<template>
  <v-container>
    <form-heading />
    <v-form
      ref="form"
      :lazy-validation="true"
      id="exhibition-contact-form"
      class="mb-12"
    >
      <validation-observer ref="validationObserver" v-slot="{ invalid }">
        <v-row class="mt-n8 mb-4">
          <v-col cols="12">
            <span class="headline pb-4">{{ $t("form.subTitleBooth") }}</span>
          </v-col>
        </v-row>

        <v-row class="mt-n8 mb-2">
          <v-col cols="12">
            <validation-provider
              :name="$t('form.fields.booth')"
              v-slot="{ errors }"
              rules="required"
            >
              <v-select
                ref="boothSelector"
                :value="selectedBoothIds"
                :items="freeBooths"
                item-value="_id"
                item-text="selectionTitle"
                :label="$t('form.fields.booth') + ' *'"
                required
                outlined
                @change="setSelectedBoothIds"
                :error-messages="errors"
                multiple
                chips
              >
              </v-select>
            </validation-provider>
          </v-col>
        </v-row>

        <template v-if="!executiveProgramUrl">
          <v-row
            class="mt-n8 mb-2"
            v-if="pageType == 'ICSMGE'"
            id="exhibition-package-selection"
          >
            <v-col cols="12">
              <span class="headline pb-4">Booked Package</span>
              <v-radio-group v-model="bookedPackage" column>
                <v-radio label="Full Package" value="FULL_PACKAGE"></v-radio>
                <v-radio
                  label="Space Only"
                  value="SPACE_ONLY_PACKAGE"
                ></v-radio>
              </v-radio-group>
              <v-alert
                v-if="bookedPackageError && !bookedPackage"
                type="error"
                outlined
              >
                {{ bookedPackageError }}
              </v-alert>
            </v-col>
          </v-row>

          <v-row class="mt-n8 mb-4">
            <v-col cols="12">
              <span class="headline pb-4">{{
                $t("form.subTitleContactPerson")
              }}</span>
            </v-col>
          </v-row>

          <v-row class="mt-n8 mb-2">
            <v-col cols="6">
              <validation-provider
                :name="$t('form.fields.firstname')"
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="firstname"
                  :label="$t('form.fields.firstname') + ' *'"
                  required
                  outlined
                  :error-messages="errors"
                ></v-text-field>
              </validation-provider>
            </v-col>
            <v-col cols="6">
              <validation-provider
                :name="$t('form.fields.lastname')"
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="lastname"
                  :label="$t('form.fields.lastname') + ' *'"
                  required
                  outlined
                  :error-messages="errors"
                ></v-text-field>
              </validation-provider>
            </v-col>
          </v-row>

          <v-row class="mt-n8 mb-2">
            <v-col cols="6">
              <validation-provider
                :name="$t('form.fields.email')"
                v-slot="{ errors }"
                vid="originalEmail"
                rules="required|email|confirmed:@confirmationEmail"
              >
                <v-text-field
                  ref="originalMail"
                  v-model="email"
                  :label="$t('form.fields.email') + ' *'"
                  required
                  outlined
                  :error-messages="errors"
                ></v-text-field>
              </validation-provider>
            </v-col>
            <v-col cols="6">
              <validation-provider
                :name="$t('form.fields.confirmationEmail')"
                v-slot="{ errors }"
                vid="confirmationEmail"
                rules="required|email|confirmed:@originalEmail"
              >
                <v-text-field
                  v-model="confirmationEmail"
                  :label="$t('form.fields.confirmationEmail') + ' *'"
                  required
                  outlined
                  :error-messages="errors"
                ></v-text-field>
              </validation-provider>
            </v-col>
          </v-row>

          <v-row class="mt-n8 mb-4">
            <v-col cols="12">
              <span class="headline pb-4">{{
                $t("form.subTitleCompany")
              }}</span>
            </v-col>
          </v-row>

          <v-row class="mt-n8 mb-2">
            <v-col cols="6">
              <validation-provider
                :name="$t('form.fields.vendorName')"
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="vendorName"
                  outlined
                  :label="$t('form.fields.vendorName') + ' *'"
                  required
                  :error-messages="errors"
                ></v-text-field>
              </validation-provider>
            </v-col>
            <v-col cols="6">
              <validation-provider
                :name="$t('form.fields.companyUid')"
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="companyUid"
                  outlined
                  :label="$t('form.fields.companyUid') + ' *'"
                  :error-messages="errors"
                ></v-text-field>
              </validation-provider>
            </v-col>
          </v-row>

          <v-row class="mt-n8 mb-2">
            <v-col cols="6">
              <validation-provider
                :name="$t('form.fields.companyStreet')"
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="companyStreet"
                  outlined
                  :label="$t('form.fields.companyStreet') + ' *'"
                  required
                  :error-messages="errors"
                ></v-text-field>
              </validation-provider>
            </v-col>
            <v-col cols="6">
              <validation-provider
                :name="$t('form.fields.companyPostalCode')"
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="companyPostalCode"
                  outlined
                  :label="$t('form.fields.companyPostalCode') + ' *'"
                  required
                  :error-messages="errors"
                ></v-text-field>
              </validation-provider>
            </v-col>
          </v-row>

          <v-row class="mt-n8 mb-2">
            <v-col cols="6">
              <validation-provider
                :name="$t('form.fields.companyTown')"
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="companyTown"
                  outlined
                  :label="$t('form.fields.companyTown') + ' *'"
                  required
                  :error-messages="errors"
                ></v-text-field>
              </validation-provider>
            </v-col>
            <v-col cols="6">
              <validation-provider
                :name="$t('form.fields.companyCountry')"
                v-slot="{ errors }"
                rules="required"
              >
                <v-autocomplete
                  :label="$t('form.fields.companyCountry') + ' *'"
                  outlined
                  :items="countries"
                  item-text="value"
                  item-value="key"
                  :error-messages="errors"
                  v-model="companyCountry"
                />
              </validation-provider>
            </v-col>
          </v-row>
          <v-row class="mt-n8">
            <v-col cols="6">
              <validation-provider
                :name="$t('form.fields.website')"
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="vendorUrl"
                  :label="$t('form.fields.website') + ' *'"
                  required
                  outlined
                  :error-messages="errors"
                ></v-text-field>
              </validation-provider>
            </v-col>
            <v-col cols="6">
              <validation-provider
                name="Logo"
                v-slot="{ errors }"
                rules="required"
              >
                <v-file-input
                  v-model="logo"
                  label="Logo *"
                  accept=".png,.jpg,.jpeg"
                  :loading="loading"
                  required
                  outlined
                  :error-messages="errors"
                ></v-file-input>
              </validation-provider>
            </v-col>
          </v-row>

          <v-row class="my-n12" v-if="!didVerify">
            <v-col>
              <div id="mcaptcha__widget-container" />
            </v-col>
          </v-row>
        </template>

        <v-row class="mt-n4 mb-2" justify="end">
          <v-col cols="3" lg="2">
            <v-btn :disabled="invalid" @click="submit" :loading="loading">
              {{ $t("form.btn.send") }}
            </v-btn>
          </v-col>
        </v-row>

        <v-dialog v-model="dialog" width="500">
          <v-card>
            <v-card-title class="white--text headline primary" primary-title>
              {{ $t("form.confirmationDialog.title") }}
            </v-card-title>

            <v-card-text class="pa-md-4">
              {{ $t("form.confirmationDialog.confirmationText") }}
            </v-card-text>

            <v-divider></v-divider>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" text @click="reload">
                {{ $t("form.confirmationDialog.buttonText") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </validation-observer>
    </v-form>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import FormHeading from "./ExhibitionForm/FormHeadline";
import {
  verificationResource,
  mcaptchaSettingsResource,
  attachmentResource,
} from "@/api/resources";
import de from "../i18n/de.json";

export default {
  data: () => ({
    id: "",
    bookedPackage: "",
    bookedPackageError: "",
    firstname: "",
    lastname: "",
    email: "",
    confirmationEmail: "",
    vendorName: "",
    companyStreet: "",
    companyPostalCode: "",
    companyTown: "",
    companyCountry: "",
    companyUid: "",
    vendorUrl: "",
    dialog: false,
    loading: false,
    didVerify: false,
    logo: null,
  }),
  async mounted() {
    this.setSelectedBoothIds([]);
    // no mcaptcha required for executive program as reservation takes place on external site
    if (this.executiveProgramUrl) {
      return;
    }
    const captchaSettings = (await mcaptchaSettingsResource.get()).data;
    let config = {
      widgetLink: new URL(captchaSettings.widgetLink),
    };
    // eslint-disable-next-line no-undef
    new mcaptchaGlue.default(config);
  },
  methods: {
    ...mapActions(["submitReservation", "setSelectedBoothIds"]),
    async submit() {
      if (this.executiveProgramUrl && this.selectedBoothIds.length > 0) {
        window.open(
          `${
            this.executiveProgramUrl
          }?selectedStandIds=${this.selectedBoothIds.join(",")}&locale=${
            this.$i18n.locale
          }`,
          "_blank"
        );
        this.setSelectedBoothIds([]);
        this.$refs.validationObserver.reset();
        return;
      }

      if (!this.bookedPackage && this.pageType === "ICSMGE") {
        this.bookedPackageError = "You must select a package";
        this.$vuetify.goTo("#exhibition-package-selection");
        return;
      }

      if (!this.didVerify) {
        const verificationToken = document.getElementById("mcaptcha__token")
          .value;
        if (!verificationToken) {
          return;
        }
        await verificationResource.post("", {
          token: verificationToken,
        });
      }
      this.didVerify = true;
      this.loading = true;

      let logoData = null;
      if (this.logo) {
        let formData = new FormData();
        formData.append("file", this.logo);
        const data = await attachmentResource.post("", formData, {
          headers: { "x-xsrf-token": this.$cookies.get("oegg-cms.csrf") },
        });
        logoData = data.data;
      }

      const selectedBoothName = this.booths
        .filter((booth) => this.selectedBoothIds.includes(booth._id))
        .map((booth) => booth.title)
        .join(", ");
      const reservation = {
        ids: this.selectedBoothIds,
        boothNumber: selectedBoothName,
        bookedPackage: this.bookedPackage,
        firstName: this.firstname,
        lastName: this.lastname,
        email: this.email,
        vendorName: this.vendorName,
        companyStreet: this.companyStreet,
        companyPostalCode: this.companyPostalCode,
        companyTown: this.companyTown,
        companyCountry: this.companyCountry,
        companyUid: this.companyUid,
        vendorUrl: this.vendorUrl,
        logo: logoData,
        locale: this.$i18n.locale,
      };
      await this.submitReservation(reservation);
      this.dialog = true;
      this.clear();
    },
    clear() {
      this.loading = false;
      this.$refs.boothSelector.internalValue = [];
      this.firstname = "";
      this.lastname = "";
      this.email = "";
      this.confirmationEmail = "";
      this.vendorName = "";
      this.companyStreet = "";
      this.companyPostalCode = "";
      this.companyTown = "";
      this.companyCountry = "";
      this.companyUid = "";
      this.vendorUrl = "";
      this.bookedPackage = "";
      this.$refs.validationObserver.reset();
      this.logo = null;
    },
    reload() {
      location.reload();
    },
  },
  computed: {
    ...mapGetters([
      "booths",
      "freeBooths",
      "selectedBoothIds",
      "pageType",
      "executiveProgramUrl",
    ]),
    countries() {
      return Object.keys(de.countries).map((countryKey) => {
        return {
          key: countryKey,
          value: this.$t(`countries.${countryKey}`),
        };
      });
    },
  },
  components: {
    FormHeading,
  },
};
</script>

<style lang="scss">
#exhibition-contact-form {
  margin-top: 50px;
}
</style>
